import { useEffect, useMemo, useState } from 'react';
import { externalSignInReturnMessage } from '@daily/client';
import { V1App } from '@daily/ui';
import type { AppStore, FixtureDataSource } from '@daily/ui';
import { takeSignInError } from '../sign-in-return.js';
import { connectAddressBar, routerAddressBar, windowAddressBar } from './addressBar.js';
import type { RouterHistoryLike } from './addressBar.js';
import { chooseDataSource } from './dataSource.js';
import type { ChosenDataSource } from './dataSource.js';

declare global {
  interface Window {
    /** Development only: the fixture switches and the store, for browser tests and manual review. */
    __v1?: { fixture: FixtureDataSource | null; store: AppStore | null };
  }
}

/**
 * The v1 app at every page address (`/`, `/home`, `/<date>`, `/<date>/<handle>`, `/<handle>`): chooses the
 * data source, then mounts the UI on it and keeps the address in step with its pages (`addressBar.ts`).
 * `history` is the router's; without one (unit tests) the window's history is used.
 */
export function V1Route({ history = null }: { readonly history?: RouterHistoryLike | null }) {
  const [chosen, setChosen] = useState<ChosenDataSource | null>(null);
  // One stable function: V1App starts its store again whenever `onStore` changes.
  const onStore = useMemo(() => (store: AppStore) => {
    if (import.meta.env.DEV) rememberStore(store);
    connectAddressBar(store, history ? routerAddressBar(history) : windowAddressBar());
  }, [history]);
  const [signInError, setSignInError] = useState<string | null>(null);
  useEffect(() => {
    // The browser's own bars take the stage's ground colour (owner, 4 October 2026). The document still names
    // the light colour of the earlier design, and index.html is not this layer's to edit (design-handoff/07).
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#0d1018');
    // A failed return from Google: its code leaves the address at once and only selects a fixed sentence.
    // The app mounts after the data source is chosen, so the sentence is there for its first render.
    const code = takeSignInError(window.location, window.history);
    if (code !== null) setSignInError(externalSignInReturnMessage(code));
    let live = true;
    void chooseDataSource(window.location.search).then((result) => {
      if (!live) return;
      if (import.meta.env.DEV) window.__v1 = { fixture: result.fixture, store: null };
      setChosen(result);
    });
    return () => { live = false; };
  }, []);
  if (!chosen) return null;
  const resetToken = window.location.pathname === '/reset-password' ? new URLSearchParams(window.location.search).get('token') : null;
  const common = { data: chosen.data, auth: chosen.auth, resetToken, onResetDone: leaveResetAddress, signInError };
  return <V1App {...common} onStore={onStore} />;
}

/** After a reset link was used, the address becomes the app's own again: the token must not stay in it. */
function leaveResetAddress() {
  if (window.location.pathname !== '/reset-password') return;
  const kept = [...new URLSearchParams(window.location.search)].filter(([key]) => key === 'fixtures' || key.startsWith('fx.'));
  window.history.replaceState(null, '', kept.length ? `/?${new URLSearchParams(kept).toString()}` : '/');
}

function rememberStore(store: AppStore) {
  if (window.__v1) window.__v1.store = store;
}
